import { useState, useEffect, RefObject } from "react";

type UseIsVisibleOptions = IntersectionObserverInit;

/**
 * A React hook that determines if the element referenced by `elementRef` is currently visible in the viewport.
 * It uses the IntersectionObserver API under the hood.
 *
 * @param elementRef - A React ref object pointing to the DOM element you want to observe.
 * @param options - IntersectionObserver options, including `root`, `rootMargin`, and `threshold`.
 * @returns A boolean indicating whether the element is visible.
 */
export function useIsVisible<T extends HTMLElement>(
  elementRef: RefObject<T>,
  options: UseIsVisibleOptions = {},
): boolean {
  const [isVisible, setIsVisible] = useState<boolean>(false);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(([entry]) => {
      setIsVisible(entry.isIntersecting);
    }, options);

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [elementRef, options]);

  return isVisible;
}
